<template>
  <component
    :is="tag"
    :style="{ maxHeight: realMaxHeight, overflow: 'hidden' }"
  >
    <span ref="contentRef" style="box-shadow: transparent 0 0;">
      <span
        ref="textRef"
        :aria-label="textContent.trim()"
        v-html="realText"
      ></span>
    </span>
    <slot v-if="isClamped || localExpanded" name="after">
      <v-btn
        class="mb-1 mt-1 p-relative gtm-click pl-2"
        color="primary"
        href="javascript:void(0)"
        variant="outlined"
        rounded
        style="top: 2px;"
        :style="{
            borderColor: `${borderColor}`,
            color: primaryColor,
            backgroundColor: 'transparent'
            }"
        size="x-small"
        height="16px"
        @click="toggle"
      >
        <template v-if="!localExpanded">
          <span key="show-more" class="d-inline-flex align-center">
            Show More
            <icon class="ms-1 p-relative" height="8" primary width="8">
              angle-right
            </icon>
          </span>
        </template>
        <template v-else>
          <span key="show-less" class="d-inline-flex align-center">
            <icon class="me-1 p-relative" height="8" primary width="8">
              angle-left
            </icon>
            Show Less
          </span>
        </template>
      </v-btn>
    </slot>
  </component>
</template>

<script>
export default {
  name: 'ClampedText',
  props: {
    tag: {
      type: String,
      default: 'div'
    },
    autoresize: {
      type: Boolean,
      default: false
    },
    maxLines: Number,
    maxChars: Number,
    maxHeight: [String, Number],
    ellipsis: {
      type: String,
      default: '…'
    },
    expanded: Boolean,
    text: {
      type: String,
      default: ''
    },
    nl2br: Boolean
  },
  data() {
    return {
      offset: null,
      localExpanded: !!this.expanded,
      textContent: this.processText(this.text || this.getSlotText()),
      resizeObserver: null,
      observedEl: null
    };
  },
  computed: {
    borderColor() {
      return 'rgb(var(--v-theme-grey-lighten-3))';
    },
    primaryColor() {
      return 'rgb(var(--v-theme-primary))';
    },
    clampedText() {
      let text = this.textContent.slice(0, this.offset).trim();
      const lastSpaceIndex = text.lastIndexOf(' ');
      if (lastSpaceIndex !== -1) text = text.slice(0, lastSpaceIndex);

      return text + this.ellipsis;
    },
    isClamped() {
      if (!this.textContent) return false;

      return this.offset !== this.textContent.length;
    },
    realText() {
      return this.isClamped ? this.clampedText : this.textContent;
    },
    realMaxHeight() {
      if (this.localExpanded) return null;

      const { maxHeight } = this;
      if (!maxHeight) return null;

      return typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight;
    }
  },
  watch: {
    text(newVal) {
      this.textContent = this.processText(newVal || this.getSlotText());
      this.update();
    },
    expanded(val) {
      this.localExpanded = val;
    },
    localExpanded(val) {
      if (val) this.clampAt(this.textContent.length);
      else this.update();

      if (this.expanded !== val) this.$emit('update:expanded', val);
    },
    isClamped: {
      handler(val) {
        this.$nextTick(() => this.$emit('clampchange', val));
      },
      immediate: true
    }
  },
  mounted() {
    this.init();

    this.$watch(
      () =>
        [this.maxLines, this.maxChars, this.maxHeight, this.ellipsis, this.isClamped].join(),
      this.update
    );
    this.$watch(
      () => [this.tag, this.textContent, this.autoresize].join(),
      this.init
    );
  },
  updated() {
    this.textContent = this.processText(this.text || this.getSlotText());
    this.applyChange();
  },
  methods: {
    processText(text) {
      if (this.nl2br) return text.replace(/\n/g, '<br>');

      return text;
    },
    getSlotText() {
      const slotFn = this.$slots.default;
      const nodes = typeof slotFn === 'function' ? slotFn() : [];
      const textNodes = nodes.filter(
        node => node != null && typeof node.children === 'string'
      );
      return textNodes
        .map(node => (typeof node.children === 'string' ? node.children : ''))
        .join('')
        .trim();
    },
    init() {
      this.offset = this.textContent.length;

      this.cleanUp();

      if (this.autoresize && !this.maxChars && this.$el) {
        const el = this.$el;
        if (el && typeof ResizeObserver !== 'undefined') {
          this.observedEl = el;
          this.resizeObserver = new ResizeObserver(() => this.update());
          this.resizeObserver.observe(el);
        }
      }
      this.update();
    },
    update() {
      if (this.localExpanded) return;

      if (this.maxChars) {
        const offset =
          this.textContent.length <= this.maxChars
            ? this.textContent.length
            : this.maxChars;
        this.clampAt(offset);
        return;
      }

      this.applyChange();
      if (this.isOverflow() || this.isClamped) this.search();
    },
    expand() {
      this.localExpanded = true;
    },
    collapse() {
      this.localExpanded = false;
    },
    toggle() {
      this.localExpanded = !this.localExpanded;
    },
    getLines() {
      const contentEl = this.$refs.contentRef;
      if (!contentEl) return 0;
      const rect = contentEl.getBoundingClientRect();
      return rect.height / 20;
    },
    isOverflow() {
      if (!this.maxLines && !this.maxHeight) return false;
      if (!this.$el) return false;

      if (this.maxLines) if (this.getLines() > this.maxLines) return true;

      if (this.maxHeight)
        if (this.$el.scrollHeight > this.$el.offsetHeight) return true;

      return false;
    },
    moveEdge(steps) {
      this.clampAt(this.offset + steps);
    },
    clampAt(offsetValue) {
      this.offset = offsetValue;
      this.applyChange();
    },
    applyChange() {
      const textEl = this.$refs.textRef;
      if (textEl) textEl.innerHTML = this.realText;
    },
    stepToFit() {
      this.fill();
      this.clamp();
    },
    fill() {
      while (
        (!this.isOverflow() || this.getLines() < 2) &&
        this.offset < this.textContent.length
      )
        this.moveEdge(1);
    },
    clamp() {
      while (this.isOverflow() && this.getLines() > 1 && this.offset > 0)
        this.moveEdge(-1);
    },
    search(...range) {
      const [from = 0, to = this.offset] = range;
      if (to - from <= 3) {
        this.stepToFit();
        return;
      }
      const target = Math.floor((to + from) / 2);
      this.clampAt(target);
      if (this.isOverflow()) this.search(from, target);
      else this.search(target, to);
    },
    cleanUp() {
      if (this.resizeObserver && this.observedEl) {
        this.resizeObserver.unobserve(this.observedEl);
        this.observedEl = null;
      }
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }
    }
  },
  beforeUnmount() {
    this.cleanUp();
  }
};
</script>
<style lang="scss" scoped>

:deep(.v-btn__overlay) {
  background: currentColor !important;
}
</style>

